import * as React from "react";

import type { SpecType } from "@/api/contracts/specifications";
import { ChangedFromBase } from "@/components/ui/changed-from-base";
import { IconButton } from "@/components/ui/icon-button";
import {
  MuiBasicTable,
  MuiBasicTableBody,
  MuiBasicTableCell,
  MuiBasicTableHead,
  MuiBasicTableRow,
} from "@/components/ui/mui-basic-table";
import { MuiCollapse } from "@/components/ui/mui-collapse";
import { MuiKeyboardArrowDownIcon, MuiKeyboardArrowUpIcon } from "@/components/ui/mui-arrow-icons";
import { MuiSkeleton } from "@/components/ui/mui-skeleton";
import { PushBadge } from "@/components/ui/push-badge";
import { MuiTypography } from "@/components/ui/mui-typography";
import { OldBesideNew } from "@/components/ui/old-beside-new";
import { TruncatedNotes } from "@/components/ui/truncated-notes";
import {
  GROUP_EXPAND_COLLAPSE_MS,
  useGroupExpansion,
  type SpecGroupExpansion,
} from "@/hooks/spec-lines";
import {
  groupBasePairs,
  groupChangeSentence,
  OLD_NOTES_MAX,
  type GroupChangeFacts,
} from "@/lib/spec-base-changes";
import { pushGroupSentence, wholeRowKind, type PushKind } from "@/lib/spec-push-preview";
import { cn } from "@/lib/utils";

// One group of a spec version — the donor's `Group`, `GroupRow` and `GroupInfo`
// (`SpecDetailsIsland/components/MainContent/Groups/`), and its skeletons
// (`components/Skeletons.tsx`) (#695). The Editor draws its groups through it,
// with its own rows as `children`.
//
//   - **the card** — a column, `1px solid #F5F5F5`, 4px radius, `mb 16`;
//   - **the header** — sticky at the top of the scroll region, z 2 (the
//     donor's `react-sticky-box`, which pins a header shorter than the pane at
//     `top: 0`), on `#F5F5F5`: `py 16`, `pl 16`, `pr 9.6`, gap 8. The chevron
//     first — a small icon button, `expand row`, up when open — then a growing
//     column of the name as an `h4` over the notes. No count, no status;
//   - **the body** — a 300ms Collapse that keeps its rows mounted while
//     folded and scrolls sideways inside the card, around one table. Its head
//     row shows at `md` and up for a group with lines: Line (three cells, `pl
//     24`), Manufacturer, Item Name, JDE Number (always — its hide gate is
//     never set here), Model Number, and Notes spanning one, plus one for the
//     changes column and one for the target-cost column. "Lines" is the
//     donor's `linesCnt` — the larger of the server count and the lines
//     loaded, kept deleted ones included (`GroupRow.tsx:47-48`,
//     `objectCache.ts:139-143`) — so a group holding only kept deleted lines
//     keeps its head row over no rows. A group with no lines is one row
//     saying so, spanning the head row's width, or 1 below `md`. (The
//     donor's span ignores the changes column; the row is alone in its
//     table, so it draws the same.) The head cells are MUI's default weight:
//     the donor's `fontWeight: 400` selector matches nothing.

// **The Editor's card** (#805, `EditableGroup.tsx`) is this card through its
// slots, not a fork: something before the chevron (the hover handle), the
// chevron's own icon pair (`ArrowDropUp` / `ArrowDropDown` there), something
// after the name column (the ⋮ menu), a bare body in place of the table (the
// Editor's has no head row and no "0 lines" row), a footer inside the collapse
// (`Add specification line`), the header row's hover handlers and classes,
// whether the header sticks (not while its card is in the air), and a memory
// of the card's own state — its fold and its note's More / Less — that the
// copy drawn in the air starts from, as the donor's lifted card is the same
// card. Left unset, the card is the details page's, unchanged.
//
// **The group flag's header** (#1083; #1060 Q5, Q7, Q8; ledger §7, "Changed
// from base widens"), an Atlas addition the donor has no group flag for: with
// `baseChanges` set — the details page sets it with "Show changes from Base"
// on, at `md` and up; the Editor's card with it on, at every width (#1104) —
// a flagged group's header takes the changed tint layered over its grey on
// either spec type (owner disposition D1 = b on PR #1109: the core grey would
// vanish into the header's own) and the info icon after its name column, its
// name pairs inside the `h4`, and its notes pair as a block, a removed note
// drawn as a notes line `~~old~~ ↳ None` under the name. In the push preview
// (#1090) the push's kinds set `baseChanges.push` and decide it instead: the
// sentence names the push, and a group it adds or removes wears the rows'
// Added / Removed badge under its notes, with no pair.

/** What a card holds of its own: its fold, and whether its note is clamped. */
export interface SpecGroupMemory {
  expanded: boolean;
  notesClamped: boolean;
}

/** The card's own state to start from, and where each change of it goes. */
export interface SpecGroupMemoryProp {
  seed?: Partial<SpecGroupMemory>;
  onChange?: (patch: Partial<SpecGroupMemory>) => void;
}

interface SpecGroupBaseProps {
  group: { id: number; name: string; notes: string | null; lineCount: number };
  expansion: SpecGroupExpansion;
  /** Before the chevron. */
  leading?: React.ReactNode;
  /** The chevron's glyphs, open and closed; `KeyboardArrowUp/Down` unset. */
  chevron?: { open: React.ReactNode; closed: React.ReactNode };
  /** After the name and notes. */
  trailing?: React.ReactNode;
  /** Inside the collapse, after the body. */
  footer?: React.ReactNode;
  /** The header row's own props — the Editor's hover. */
  header?: Pick<React.ComponentProps<"div">, "className" | "onMouseEnter" | "onMouseLeave">;
  /** Whether the header sticks to the top of the scroll region; on by default. */
  sticky?: boolean;
  memory?: SpecGroupMemoryProp;
  /**
   * The group's change marks against its base group, and the spec's type for
   * the icon's sentence. Unset draws none: the switch off, and the details
   * page below `md`.
   */
  baseChanges?: {
    specType: SpecType;
    group: GroupChangeFacts;
    /**
     * The push preview's kinds for the group, and who its sentence names
     * (#1090): they mark the header in place of the group's flag.
     */
    push?: { by: string; kinds: readonly PushKind[] };
  };
}

/** The details page's body: the lines table around the group's rows. */
interface SpecGroupTableProps {
  showChanges: boolean;
  /** The target-cost switch's state — it widens the table in the donor. */
  showTargetCost: boolean;
  isDesktop: boolean;
  /** How many of the group's lines are loaded, whether or not they render. */
  loadedLineCount: number;
  /** The group's rows. */
  children?: React.ReactNode;
}

/** The Editor's body, drawn as given. */
interface SpecGroupBodyProps {
  body: React.ReactNode;
}

type SpecGroupProps = SpecGroupBaseProps & (SpecGroupTableProps | SpecGroupBodyProps);

export function SpecGroup(props: SpecGroupProps) {
  const {
    group,
    expansion,
    leading,
    chevron,
    trailing,
    footer,
    header,
    sticky = true,
    memory,
    baseChanges,
  } = props;
  const push = baseChanges?.push;
  const sentence = push
    ? pushGroupSentence(push.by, push.kinds, baseChanges.group.changedFields)
    : baseChanges
      ? groupChangeSentence(baseChanges.group, baseChanges.specType)
      : null;
  // A group the push adds or removes is the change itself, with no pair.
  const badge = push ? wholeRowKind(push.kinds) : null;
  const pairs = sentence && baseChanges && !badge ? groupBasePairs(baseChanges.group) : {};
  const notes = (
    <TruncatedNotes
      notes={group.notes}
      className="mb-0"
      defaultClamped={memory?.seed?.notesClamped}
      onClampedChange={(notesClamped) => memory?.onChange?.({ notesClamped })}
    />
  );
  const { expanded, toggle } = useGroupExpansion(group.id, expansion, memory?.seed?.expanded);
  const report = React.useEffectEvent((now: boolean) => memory?.onChange?.({ expanded: now }));
  React.useEffect(() => report(expanded), [expanded]);

  return (
    <div
      data-testid={`group-${group.id}`}
      className="border-mui-grey-100 relative mb-4 flex flex-col rounded-[4px] border border-solid"
    >
      <div
        data-testid={`group-${group.id}-header`}
        className={cn(sticky ? "sticky top-0" : "relative", "z-2")}
      >
        <div
          {...header}
          className={cn(
            "bg-mui-grey-100 relative z-1 flex flex-row items-center justify-between gap-2 py-4 pr-[9.6px] pl-4",
            // The row tint is translucent, so it layers over the grey as it
            // does over a row's white.
            sentence &&
              "bg-[image:linear-gradient(var(--color-spec-changed-from-base),var(--color-spec-changed-from-base))]",
            header?.className,
          )}
        >
          {leading}
          <IconButton aria-label="expand row" size="small" onClick={toggle}>
            {expanded
              ? (chevron?.open ?? <MuiKeyboardArrowUpIcon />)
              : (chevron?.closed ?? <MuiKeyboardArrowDownIcon />)}
          </IconButton>
          <div className="flex grow flex-col">
            <MuiTypography variant="h4" component="h4">
              {pairs.name !== undefined ? (
                <OldBesideNew old={pairs.name}>{group.name}</OldBesideNew>
              ) : (
                group.name
              )}
            </MuiTypography>
            {pairs.notes !== undefined ? (
              <OldBesideNew old={pairs.notes} cutAt={OLD_NOTES_MAX} layout="block">
                {group.notes ? notes : null}
              </OldBesideNew>
            ) : (
              notes
            )}
            {badge ? <PushBadge kind={badge} /> : null}
          </div>
          {sentence && baseChanges ? (
            <ChangedFromBase specType={baseChanges.specType} sentence={sentence} />
          ) : null}
          {trailing}
        </div>
      </div>

      <MuiCollapse
        in={expanded}
        timeoutMs={GROUP_EXPAND_COLLAPSE_MS}
        unmountOnExit={false}
        // The details page's body scrolls sideways inside its card
        // (`GroupRow.tsx:79`); the Editor's Collapse sets no overflow
        // (`EditableGroup.tsx:165-171`), so its rows spill.
        overflowAuto={!("body" in props)}
        data-testid={`group-${group.id}-lines`}
      >
        {"body" in props ? props.body : <SpecGroupTable {...props} />}
        {footer}
      </MuiCollapse>
    </div>
  );
}

function SpecGroupTable({
  group,
  showChanges,
  showTargetCost,
  isDesktop,
  loadedLineCount,
  children,
}: SpecGroupTableProps & Pick<SpecGroupBaseProps, "group">) {
  const hasLines = Math.max(group.lineCount, loadedLineCount) > 0;
  // The head row's width: seven cells before Notes, which spans the changes
  // and target-cost columns too.
  const notesSpan = (showChanges ? 1 : 0) + (showTargetCost ? 1 : 0) + 1;
  const headColumns = 7 + notesSpan;

  return (
    <MuiBasicTable>
      {isDesktop && hasLines ? (
        <MuiBasicTableHead>
          <MuiBasicTableRow>
            <MuiBasicTableCell head colSpan={3} className="pl-6">
              Line
            </MuiBasicTableCell>
            <MuiBasicTableCell head>Manufacturer</MuiBasicTableCell>
            <MuiBasicTableCell head>Item Name</MuiBasicTableCell>
            <MuiBasicTableCell head>JDE Number</MuiBasicTableCell>
            <MuiBasicTableCell head>Model Number</MuiBasicTableCell>
            <MuiBasicTableCell head colSpan={notesSpan}>
              Notes
            </MuiBasicTableCell>
          </MuiBasicTableRow>
        </MuiBasicTableHead>
      ) : null}
      <MuiBasicTableBody>
        {hasLines ? (
          children
        ) : (
          <MuiBasicTableRow>
            <MuiBasicTableCell colSpan={isDesktop ? headColumns : 1}>
              There are 0 specification lines in this group.
            </MuiBasicTableCell>
          </MuiBasicTableRow>
        )}
      </MuiBasicTableBody>
    </MuiBasicTable>
  );
}

/** `LineSkeleton`'s seven blocks, left to right (`Skeletons.tsx:93-104`). */
const LINE_SKELETON_WIDTHS = [
  "w-[28%]",
  "w-[8%]",
  "w-[12%]",
  "w-[12%]",
  "w-[12%]",
  "w-[10%]",
  "w-[20%]",
];

/**
 * `GroupSkeleton` (`Skeletons.tsx:83-104`): an 80px rounded bar on `grey.300`
 * at MUI's default pulse, over two rows of seven 80px rectangular wave blocks
 * on `grey.100`, 4px apart and 4px under each row. The widths sum past 100%
 * with the gaps, so the blocks shrink to fit, as they do in the donor.
 */
export function SpecGroupSkeleton() {
  return (
    <div data-testid="spec-group-skeleton" className="flex flex-col">
      <MuiSkeleton variant="rounded" className="bg-lennar-grey-300 h-20 w-full" />
      {[0, 1].map((row) => (
        <div key={row} className="mb-1 flex flex-row gap-1">
          {/* A fixed list whose widths repeat: the index is the identity. */}
          {LINE_SKELETON_WIDTHS.map((width, index) => (
            <MuiSkeleton
              key={index}
              variant="rectangular"
              animation="wave"
              className={cn("bg-mui-grey-100 h-20", width)}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * `LineSearchSkeleton` and `LinePaginationSkeleton`'s body (`Skeletons.tsx:20-
 * 29,62-67`): two group skeletons 32px apart.
 */
export function SpecGroupsSkeletonPair({
  ref,
  testId,
}: {
  ref?: React.Ref<HTMLDivElement>;
  testId: string;
}) {
  return (
    <div ref={ref} data-testid={testId} className="flex w-full flex-col gap-8">
      <SpecGroupSkeleton />
      <SpecGroupSkeleton />
    </div>
  );
}
